import React, { useEffect, useState } from 'react';
import { Loader2, Download } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const eur = (n) => `€${n.toLocaleString('en-GB', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
const monthName = (k) => new Date(`${k}-15T12:00:00`).toLocaleDateString('en-GB', { month: 'long', year: 'numeric' });

const Stat = ({ label, value, testid, tone = 'text-[#0f172a]' }) => (
  <div className="bg-white border border-[#e2e8f0] p-4">
    <p className="text-xs uppercase tracking-wider text-[#64748b]">{label}</p>
    <p className={`text-2xl font-semibold mt-1 ${tone}`} data-testid={testid}>{value}</p>
  </div>
);

const downloadCsv = async (month, headers, setError, onUnauthorized) => {
  try {
    const r = await fetch(`${API_URL}/api/admin/export?month=${month}`, { headers: headers() });
    if (r.status === 401) { onUnauthorized(); return; }
    if (!r.ok) throw new Error();
    const url = URL.createObjectURL(await r.blob());
    const a = document.createElement('a');
    a.href = url; a.download = `dr-castellon-${month}.csv`;
    document.body.appendChild(a); a.click(); a.remove();
    URL.revokeObjectURL(url);
  } catch { setError(`Could not download the ${month} spreadsheet. Please try again.`); }
};

export const RevenueSummary = ({ headers, onUnauthorized, refreshKey }) => {
  const [data, setData] = useState(null);
  const [error, setError] = useState('');
  const [dlError, setDlError] = useState('');

  useEffect(() => {
    setError('');
    fetch(`${API_URL}/api/admin/revenue?months=12`, { headers: headers() })
      .then(async r => {
        if (r.status === 401) { onUnauthorized(); return; }
        if (!r.ok) throw new Error();
        setData(await r.json());
      })
      .catch(() => setError('Could not load the monthly summary. Please try again.'));
  }, [headers, onUnauthorized, refreshKey]);

  if (error) return <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm" data-testid="revenue-error">{error}</div>;
  if (!data) return <Loader2 className="animate-spin text-[#b8860b]" />;

  const current = data.months[0];
  const maxNet = Math.max(1, ...data.months.map(m => m.income));

  return (
    <div className="space-y-6" data-testid="revenue-summary">
      {dlError && <div className="bg-red-50 border border-red-200 text-red-700 px-4 py-3 text-sm" data-testid="revenue-export-error">{dlError}</div>}
      <div>
        <h2 className="font-semibold text-[#0f172a] mb-3">{monthName(current.month)}</h2>
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
          <Stat label="Appointments" value={current.appointments} testid="revenue-current-appointments" />
          <Stat label="Income" value={eur(current.income)} testid="revenue-current-income" />
          <Stat label="Refunds" value={eur(current.refunds)} testid="revenue-current-refunds" tone="text-red-600" />
          <Stat label="Net" value={eur(current.net)} testid="revenue-current-net" tone="text-[#b8860b]" />
        </div>
      </div>

      <div className="bg-white border border-[#e2e8f0]">
        <div className="flex items-center justify-between px-4 py-3 border-b border-[#e2e8f0]">
          <h3 className="font-semibold text-[#0f172a]">Last 12 months</h3>
          <span className="text-sm text-[#475569]" data-testid="revenue-total-net">Net total {eur(data.totals.net)}</span>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#f8fafc] text-left text-xs uppercase tracking-wider text-[#64748b] whitespace-nowrap">
              <tr>
                <th className="px-4 py-2">Month</th><th className="px-4 py-2 text-right">Visits</th>
                <th className="px-4 py-2 text-right">Home / Tele</th><th className="px-4 py-2 text-right">Cancelled</th>
                <th className="px-4 py-2 text-right">Income</th><th className="px-4 py-2 text-right">Refunds</th>
                <th className="px-4 py-2 text-right">Net</th><th className="px-4 py-2 w-40"></th><th className="px-4 py-2"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#e2e8f0]">
              {data.months.map(m => (
                <tr key={m.month} data-testid={`revenue-row-${m.month}`}>
                  <td className="px-4 py-2 whitespace-nowrap">{monthName(m.month)}</td>
                  <td className="px-4 py-2 text-right">{m.appointments}</td>
                  <td className="px-4 py-2 text-right text-[#64748b]">{m.home_visits} / {m.telemedicine}</td>
                  <td className="px-4 py-2 text-right text-[#64748b]">{m.cancelled}</td>
                  <td className="px-4 py-2 text-right">{eur(m.income)}</td>
                  <td className="px-4 py-2 text-right text-red-600">{m.refunds ? `−${eur(m.refunds)}` : '—'}</td>
                  <td className="px-4 py-2 text-right font-medium" data-testid={`revenue-net-${m.month}`}>{eur(m.net)}</td>
                  <td className="px-4 py-2">
                    <div className="h-2 bg-[#f1f5f9]"><div className="h-2 bg-[#b8860b] transition-[width] duration-500" style={{ width: `${Math.max(0, m.net) / maxNet * 100}%` }} /></div>
                  </td>
                  <td className="px-4 py-2 text-right">
                    <button onClick={() => { setDlError(""); downloadCsv(m.month, headers, setDlError, onUnauthorized); }} data-testid={`revenue-export-${m.month}`}
                      title="Download spreadsheet for your accountant"
                      className="inline-flex items-center gap-1 text-xs text-[#0f172a] hover:text-[#b8860b] transition-colors">
                      <Download size={14} /> CSV
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
        <p className="px-4 py-3 text-xs text-[#94a3b8] border-t border-[#e2e8f0]">
          Visits are counted in the month of the appointment. Income is counted in the month it was paid, and refunds in the month they were made. Unpaid bookings are not included. All times are Ibiza time. CSV files open in Excel, Numbers and Google Sheets. A summary of last month is emailed to you on the 1st of each month.
        </p>
      </div>
    </div>
  );
};
